import React, { useEffect, useLayoutEffect, useState, useRef } from 'react';
import debounce from 'lodash.debounce';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import Icon from './Icon';

const CarouselWrapper = styled.div`
  min-height: 120px;
  min-width: 200px;
  padding: 16px 32px;
  position: relative;
  width: 100%;
`;

const Navigation = styled.div`
  align-items: center;
  background: none;
  border: none;
  bottom: 0;
  display: flex;
  position: absolute;
  top: 0;
  bottom: 32px;
  width: 118px;
  z-index: 1;
`;

const Left = styled(Navigation)`
  justify-content: flex-start;
  left: 0;
  padding-left: 15px;
  ${({ $active }) =>
    !$active &&
    `background:  linear-gradient(90deg, var(--white) 3%, transparent 97%);`}
`;

const Right = styled(Navigation)`
  justify-content: flex-end;
  padding-right: 15px;
  right: 0;
  ${({ $active }) =>
    !$active &&
    `background: linear-gradient(270deg,var(--white) 3%, transparent 97%);`}
`;

const Arrow = styled.button`
  align-items: center;
  background-color: var(--white);
  border-radius: 50%;
  border: 1px solid var(--noreaster);
  box-shadow: var(--level-2);
  color: var(--blurple);
  display: flex;
  font-size: var(--medium-large);
  height: 40px;
  justify-content: center;
  width: 40px;
  ${({ disabled }) => disabled && 'display: none;'}
`;

const Body = styled.div`
  align-items: center;
  display: flex;
  gap: 24px;
`;

const Wrapper = styled.div`
  display: flex;
  height: 100%;
  justify-content: flex-start;
  overflow-x: auto;
  padding: 16px 0;
  position: relative;
  width: 100%;
`;

const Item = styled.div`
  display: flex;
  overflow: hidden;
`;

const ITEM_GAP = 24;
const SCROLL_INCREMENT = 200;
const ONE_FRAME = 16;

export const Carousel = ({ items }) => {
  // state for current scroll position
  const [currentPosition, setCurrentPosition] = useState(0);
  const [previousActive, setPreviousActive] = useState(false);
  const [nextActive, setNextActive] = useState(false);
  const [showArrow, setShowArrow] = useState(false);
  const [wrapperWidth, setWrapperWidth] = useState();

  const wrapper = useRef();
  const body = useRef();

  useEffect(() => {
    const handler = debounce(() => {
      wrapper.current.scrollLeft = 0;
      setWrapperWidth(wrapper.current?.offsetWidth);
    }, ONE_FRAME);

    window.addEventListener('resize', handler);

    return () => {
      window.removeEventListener('resize', handler);
    };
  }, []);

  useLayoutEffect(() => {
    const { offsetWidth, scrollWidth } = wrapper.current;

    setPreviousActive(currentPosition <= 0);
    setNextActive(scrollWidth - currentPosition - offsetWidth <= ITEM_GAP / 2);
    setWrapperWidth(scrollWidth);
  }, [wrapperWidth, currentPosition, items]);

  useLayoutEffect(() => {
    const { parentElement, offsetWidth } = body.current;
    const bodyWidth = items.length * SCROLL_INCREMENT;
    const parentWidth = parentElement?.offsetWidth;

    setShowArrow(parentWidth <= offsetWidth || parentWidth <= bodyWidth);
  }, [wrapperWidth, currentPosition, items]);

  const previous = () => {
    // scroll the wrapper to left by the value of scroll increment
    wrapper.current.scrollLeft =
      wrapper.current.scrollLeft - SCROLL_INCREMENT - ITEM_GAP;
    setCurrentPosition(wrapper.current.scrollLeft);
  };
  const next = () => {
    // scroll the wrapper to right by the value of scroll increment
    wrapper.current.scrollLeft =
      wrapper.current.scrollLeft + SCROLL_INCREMENT + ITEM_GAP;
    setCurrentPosition(wrapper.current.scrollLeft);
  };

  const onScroll = e => {
    setCurrentPosition(e.target.scrollLeft);
  };

  return (
    <CarouselWrapper>
      {showArrow && (
        <Left $active={previousActive}>
          <Arrow
            disabled={previousActive}
            onClick={previous}
            role="button"
            aria-label="previous"
          >
            <Icon icon="chevron-left" />
          </Arrow>
        </Left>
      )}
      <Wrapper ref={wrapper} onScroll={onScroll}>
        <Body ref={body}>
          {items.map(slide => {
            return (
              <Item key={slide.title} title={slide.title} tabIndex="0">
                {slide.element}
              </Item>
            );
          })}
        </Body>
      </Wrapper>
      {showArrow && (
        <Right $active={nextActive}>
          <Arrow
            disabled={nextActive}
            onClick={next}
            role="button"
            aria-label="next"
          >
            <Icon icon="chevron-right" />
          </Arrow>
        </Right>
      )}
    </CarouselWrapper>
  );
};
const ItemShape = {
  title: PropTypes.string,
  element: PropTypes.element,
};
Carousel.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape(ItemShape)),
};

Carousel.displayName = 'Carousel';

export default styled(Carousel)``;
